import { FlexColumnCentered } from '@fox/layout';
import { DotLottie, DotLottieReact, DotLottieReactProps } from '@lottiefiles/dotlottie-react';
import { useState } from 'react';
import styled from 'styled-components';
import { Card, CardActionArea, CardContent } from '../card';
import { Typography } from '../typography';

/** These lottie files are stored in our static assets
 * If a new lottie animation is added to static assets please add it here
 */
export type BrandAnimationNames =
	| 'automation'
	| 'communication'
	| 'idea'
	| 'issue'
	| 'support'
	| 'lists'
	| 'networks'
	| 'payments'
	| 'piplines'
	| 'profiles'
	| 'reports'
	| 'search'
	| 'security'
	| 'unlock-potential';

// Do not touch without consultation. These are specified by design team.
export const DOTTIE_SIZE_MAP = {
	small: '70px',
	medium: '105px',
	large: '140px'
} as const;

/** This is currently only intended to be used in TileIcon, not seperately. Please consult UI/UX before using BrandAnimation. */
function BrandAnimation({
	size = 'medium',
	name,
	onDotLottieRef,
	...props
}: Omit<DotLottieReactProps, 'src' | 'width' | 'dotLottieRefCallback'> & {
	name: BrandAnimationNames;
	size?: keyof typeof DOTTIE_SIZE_MAP;
	onDotLottieRef?: (dotLottie: DotLottie | null) => void;
}) {
	return (
		<DotLottieReact
			height={DOTTIE_SIZE_MAP[size]}
			src={`https://static.sfox.au/images/animations/icon_${name}.lottie`}
			dotLottieRefCallback={onDotLottieRef}
			{...props}
		/>
	);
}

const StyledCard = styled(Card)`
	box-sizing: border-box;
	/* transparent border to prevent layout shift */
	border: 1px solid transparent;
	transition:
		background-color 150ms ease,
		box-shadow 150ms ease;
	&:hover {
		background-color: ${props => props.theme.palette.secondary['_states']['hover']};
		border: 1px solid ${props => props.theme.palette.primary['_states']['outlinedBorder']};
		outline: none;
	}
	&:focus {
		border: 1px solid ${props => props.theme.palette.swiftfox.blue['20-frostedAzure']};
		outline: none;
	}
	&:active {
		border: 1px solid ${props => props.theme.palette.swiftfox.blue['20-frostedAzure']};
		outline: none;
	}
`;

export const TileIcon = ({
	label,
	animationName,
	onClick,
	iconSize
}: {
	label: string;
	animationName: BrandAnimationNames;
	onClick: (() => void) | (() => Promise<void>);
	iconSize?: keyof typeof DOTTIE_SIZE_MAP;
}) => {
	const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);

	const handleMouseEnter = () => {
		dotLottie?.setFrame(0);
		dotLottie?.play();
	};

	return (
		<StyledCard sx={{ flex: 1, borderRadius: '12px' }}>
			<CardActionArea onClick={onClick} onMouseEnter={handleMouseEnter}>
				<CardContent
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						gap: 2,
						py: 4
					}}
				>
					<BrandAnimation size={iconSize ?? 'large'} name={animationName} onDotLottieRef={setDotLottie} />
					<Typography variant="h6" fontWeight="medium">
						{label}
					</Typography>
				</CardContent>
			</CardActionArea>
		</StyledCard>
	);
};

//yeah just want this image really
export const TileIconMobile = ({
	label,
	animationName,
	onClick
}: {
	label: string;
	animationName: BrandAnimationNames;
	onClick: (() => void) | (() => Promise<void>);
}) => {
	const [_, setDotLottie] = useState<DotLottie | null>(null);
	return (
		<FlexColumnCentered onClick={onClick} style={{ cursor: 'pointer', maxWidth: '20rem' }}>
			<BrandAnimation size="large" name={animationName} onDotLottieRef={setDotLottie} />
			<Typography variant="h6" fontWeight="medium">
				{label}
			</Typography>
		</FlexColumnCentered>
	);
};
